import { useCallback, useEffect, useState } from 'react';
import {
  BarChart3Icon,
  CheckCircle2Icon,
  ChevronLeftIcon,
  ClockIcon,
  DownloadIcon,
  Loader2Icon,
  RefreshCwIcon,
  SearchIcon,
  UserIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import type { ProgressStatus } from '@kennisbank/shared';
import { ApiError } from '../../api/client.js';
import { type DashboardRow, getDashboard, resetRetakes } from './AdminElearningApi.js';
import { AdminProgressChip } from './AdminElearningChips.js';

/**
 * Voortgangsdashboard per cursus — geport van MoveLogic
 * `CourseProgressDashboard`. Toont per deelnemer voortgangspercentage,
 * examenstatus, beste score, aantal pogingen en laatste activiteit, met een
 * "herkansingen resetten"-knop per deelnemer (gezakt / examenklaar).
 *
 * Naam en e-mail komen uit het gebruikersprofiel (de backend voegt ze toe);
 * ontbreekt het profiel, dan tonen we de userId. Cursus zonder examen: geen
 * examenkolommen en 'Afgerond' i.p.v. 'Geslaagd' (overzicht voor Linda,
 * 26 okt 2026). Download als CSV voor audits.
 */
export function AdminCourseDashboard({
  courseId,
  courseName,
  onBack,
}: {
  courseId: string;
  courseName?: string;
  onBack: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<DashboardRow[] | null>(null);
  const [hasExam, setHasExam] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resettingUser, setResettingUser] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProgressStatus>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDashboard(courseId);
      setRows(res.rows);
      setHasExam(res.hasExam);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Laden mislukt.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleReset(userId: string): Promise<void> {
    if (!window.confirm('Weet je zeker dat je de herkansingen voor deze deelnemer wilt resetten?'))
      return;
    setResettingUser(userId);
    try {
      await resetRetakes(courseId, userId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Resetten mislukt.');
    } finally {
      setResettingUser(null);
    }
  }

  const total = rows?.length ?? 0;
  const passed = rows?.filter((r) => r.status === 'geslaagd').length ?? 0;
  const busy = rows?.filter((r) => r.status === 'bezig' || r.status === 'examenklaar').length ?? 0;
  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

  const klaarLabel = hasExam ? 'Geslaagd' : 'Afgerond';
  const q = search.trim().toLowerCase();
  const filtered = (rows ?? [])
    .filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (q && ![r.name, r.email, r.userId].some((v) => v?.toLowerCase().includes(q))) return false;
      return true;
    })
    .sort((a, b) => (a.name ?? a.userId).localeCompare(b.name ?? b.userId, 'nl'));

  function downloadCsv(): void {
    const datum = (iso: string | null): string =>
      iso ? new Date(iso).toLocaleDateString('nl-NL', { timeZone: 'Europe/Amsterdam' }) : '';
    const kop = ['Naam', 'E-mail', 'Status', 'Voortgang %', 'Gestart', 'Afgerond op'];
    const regels = filtered.map((r) => [
      r.name ?? r.userId,
      r.email ?? '',
      r.status === 'geslaagd' ? klaarLabel : r.status,
      String(r.progressPercentage),
      datum(r.startedAt),
      r.status === 'geslaagd' ? datum(r.completedAt) : '',
    ]);
    // Puntkomma + BOM: zo opent Excel met Nederlandse instellingen het netjes.
    const csv = [kop, ...regels]
      .map((rij) => rij.map((v) => `"${v.replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Voortgang ${courseName ?? 'cursus'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6">
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Terug"
          className="shrink-0 rounded-md p-1.5 text-ink-700 hover:bg-brand-50"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Voortgang</h1>
          {courseName && <p className="mt-0.5 text-sm text-ink-700/70">{courseName}</p>}
        </div>
        <button
          type="button"
          onClick={downloadCsv}
          disabled={!rows?.length}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-40"
        >
          <DownloadIcon size={14} /> Download (CSV)
        </button>
        <button
          type="button"
          onClick={() => void load()}
          aria-label="Vernieuwen"
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50"
        >
          <RefreshCwIcon size={14} /> Vernieuwen
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2Icon className="h-6 w-6 animate-spin text-ink-700/40" />
        </div>
      ) : total === 0 ? (
        <div className="rounded-xl border border-brand-100 bg-white py-16 text-center shadow-card">
          <UsersIcon className="mx-auto mb-4 h-10 w-10 text-ink-700/30" />
          <h3 className="mb-1 text-lg font-semibold text-ink-900">Nog geen deelnemers</h3>
          <p className="mx-auto max-w-sm text-sm text-ink-700/70">
            Er is nog geen voortgang. Zodra een gebruiker de cursus start, verschijnt die hier.
          </p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatCard icon={<UsersIcon size={18} />} value={total} label="Deelnemers" />
            <StatCard icon={<ClockIcon size={18} />} value={busy} label="Bezig" />
            <StatCard icon={<CheckCircle2Icon size={18} />} value={passed} label={klaarLabel} />
            <StatCard
              icon={<BarChart3Icon size={18} />}
              value={`${passRate}%`}
              label={hasExam ? 'Slagingspercentage' : 'Afrondingspercentage'}
            />
          </div>

          {/* Filters */}
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-0.5 rounded-lg border border-brand-100 bg-brand-50/50 p-0.5">
              {(hasExam
                ? ([
                    { value: 'all', label: 'Alle' },
                    { value: 'bezig', label: 'Bezig' },
                    { value: 'examenklaar', label: 'Examenklaar' },
                    { value: 'geslaagd', label: 'Geslaagd' },
                    { value: 'gezakt', label: 'Gezakt' },
                  ] as const)
                : ([
                    { value: 'all', label: 'Alle' },
                    { value: 'bezig', label: 'Bezig' },
                    { value: 'geslaagd', label: 'Afgerond' },
                  ] as const)
              ).map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setStatusFilter(t.value)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    statusFilter === t.value
                      ? 'bg-white text-brand-800 shadow-sm'
                      : 'text-ink-700/70 hover:text-ink-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="relative max-w-xs sm:w-64">
              <SearchIcon
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-700/40"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op naam of e-mail…"
                className="w-full rounded-lg border border-ring2-border bg-white py-2 pl-9 pr-8 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Wissen"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-700/40 hover:text-ink-700"
                >
                  <XIcon size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white shadow-card">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-brand-50/50 text-left text-[11px] uppercase tracking-wider text-ink-800/60">
                <tr>
                  <th className="px-4 py-3">Gebruiker</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Voortgang</th>
                  {hasExam && <th className="px-4 py-3 text-right">Pogingen</th>}
                  {hasExam && <th className="px-4 py-3 text-right">Beste score</th>}
                  <th className="px-4 py-3">Gestart</th>
                  <th className="px-4 py-3">{hasExam ? 'Geslaagd op' : 'Afgerond op'}</th>
                  <th className="px-4 py-3">Laatste activiteit</th>
                  <th className="px-4 py-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {filtered.map((r) => {
                  const canReset = r.status === 'gezakt' || r.status === 'examenklaar';
                  return (
                    <tr key={r.userId} className="hover:bg-brand-50/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-100 bg-brand-50 text-ink-700/60">
                            <UserIcon size={15} />
                          </span>
                          {r.name ? (
                            <span>
                              <span className="block font-medium text-ink-900">{r.name}</span>
                              {r.email && (
                                <span className="block text-xs text-ink-700/60">{r.email}</span>
                              )}
                            </span>
                          ) : (
                            <span className="font-mono text-xs text-ink-900/80">{r.userId}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <AdminProgressChip
                          status={r.status}
                          label={r.status === 'geslaagd' ? klaarLabel : undefined}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 overflow-hidden rounded-full bg-brand-50">
                            <div
                              className="h-full rounded-full bg-brand-600 transition-all"
                              style={{ width: `${Math.min(r.progressPercentage, 100)}%` }}
                            />
                          </div>
                          <span className="w-10 text-xs text-ink-700/70">
                            {r.progressPercentage}%
                          </span>
                          <span className="text-xs text-ink-700/50">
                            {r.videoQuestionsTotal > 0
                              ? `(${r.videoQuestionsAnswered}/${r.videoQuestionsTotal} vragen)`
                              : `(${r.completedLessons}/${r.totalLessons})`}
                          </span>
                        </div>
                      </td>
                      {hasExam && (
                        <td className="px-4 py-3 text-right text-ink-900/80">
                          {r.examAttemptsCount}
                        </td>
                      )}
                      {hasExam && (
                        <td className="px-4 py-3 text-right text-ink-900/80">
                          {r.bestExamScore != null ? `${r.bestExamScore}%` : '—'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-ink-700/70">
                        {new Date(r.startedAt).toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-4 py-3 text-ink-700/70">
                        {r.status === 'geslaagd' && r.completedAt
                          ? new Date(r.completedAt).toLocaleDateString('nl-NL')
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-ink-700/70">
                        {r.updatedAt ? new Date(r.updatedAt).toLocaleString('nl-NL') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canReset && (
                          <button
                            type="button"
                            onClick={() => void handleReset(r.userId)}
                            disabled={resettingUser === r.userId}
                            className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
                          >
                            {resettingUser === r.userId ? (
                              <Loader2Icon size={13} className="animate-spin" />
                            ) : (
                              <RefreshCwIcon size={13} />
                            )}
                            Herkansingen resetten
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={hasExam ? 9 : 7} className="px-4 py-8 text-center text-ink-700/60">
                      Geen deelnemers gevonden met deze filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({
  icon,
  value,
  label,
}: {
  icon: JSX.Element;
  value: number | string;
  label: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-brand-100 bg-white p-4 shadow-card">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
        {icon}
      </span>
      <div>
        <p className="text-2xl font-bold text-ink-900">{value}</p>
        <p className="text-xs text-ink-700/60">{label}</p>
      </div>
    </div>
  );
}
